<template>
    <div
        v-click-outside="closeDropDown"
        class="lsn-loader-dropdown-button lsn-btn"
        :class="buttonClass"
    >
        <div
            class="lsn-loader-dropdown-button__label"
            :tabindex="tabindex"
            @click="selectOption(defaultOption)"
            @keypress.enter="selectOption(defaultOption)"
        >
            {{ defaultOption.label }}
        </div>

        <div class="lsn-loader-dropdown-button__separator"></div>

        <div
            class="lsn-loader-dropdown-button__chevron"
            :tabindex="tabindex"
            @click.stop="toggleDropDown"
            @keypress.enter.stop="toggleDropDown"
        >
            <mdi-icon icon="mdiMenuDown" class="w-4 h-4" />
        </div>

        <div v-if="isOpen" class="lsn-loader-dropdown-button__dropdown">
            <div
                v-for="(option, index) in options"
                :key="index"
                class="lsn-loader-dropdown-button__item"
                :tabindex="tabindex"
                @click.stop="selectOption(option)"
                @keypress.enter.stop="selectOption(option)"
            >
                {{ option.label }}
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';

const props = defineProps({
    isLoading:
    {
        type:    Boolean,
        default: false,
    },

    class:
    {
        type:    [String, Array, Object],
        default: '',
    },

    tabindex:
    {
        type:    Number,
        default: 0,
    },

    options:
    {
        type:     Array,
        required: true,
    },
});

const isOpen = ref(false);

const defaultOption = computed(() =>
{
    for(let i = props.options.length - 1; i >= 0; i--)
    {
        if(props.options[i].default === true)
        {
            return props.options[i];
        }
    }

    return props.options[0];
});

const buttonClass = computed(() =>
{
    const classes = {
        'lsn-loader-dropdown-button--loading': props.isLoading,
        'lsn-loader-dropdown-button--open':    isOpen.value,
    };

    if(typeof props.class === 'string')
    {
        classes[props.class] = true;
    }
    else if(Array.isArray(props.class))
    {
        for(const theClass of props.class)
        {
            classes[theClass] = true;
        }
    }
    else if(typeof props.class === 'object')
    {
        for(const theClass in props.class)
        {
            classes[theClass] = props.class[theClass];
        }
    }

    return classes;
});

function toggleDropDown()
{
    if(props.isLoading)
    {
        return;
    }

    isOpen.value = !isOpen.value;
}

function closeDropDown()
{
    isOpen.value = false;
}

function selectOption(option)
{
    option.onclick();
    closeDropDown();
}

onMounted(() =>
{
    // Validate at least one option is present
    if(props.options.length === 0)
    {
        throw new Error('LsnLoaderDropDownButton: options must not be empty.');
    }

    // Validate options structure
    for(const [i, option] of props.options.entries())
    {
        if(typeof option.label !== 'string' || option.label.trim() === '')
        {
            throw new TypeError(`LsnLoaderDropDownButton: option[${i}].label must be a non-empty string.`);
        }

        if(typeof option.onclick !== 'function')
        {
            throw new TypeError(`LsnLoaderDropDownButton: option[${i}].onclick must be a function.`);
        }

        if('default' in option && typeof option.default !== 'boolean')
        {
            throw new TypeError(`LsnLoaderDropDownButton: option[${i}].default must be a boolean.`);
        }
    }
});
</script>

<style scoped>
@reference "#main.css";

.lsn-loader-dropdown-button
{
    @apply relative p-0 outline-none;

    &:not(.lsn-loader-dropdown-button--loading):has(.lsn-loader-dropdown-button__label:focus-visible),
    &:not(.lsn-loader-dropdown-button--loading):has(.lsn-loader-dropdown-button__chevron:focus-visible)
    {
        @apply ring-1 ring-offset-1 ring-primary-500 bg-primary-500 text-white;
    }
}

.lsn-loader-dropdown-button--loading
{
    @apply cursor-not-allowed;

    & > .lsn-loader-dropdown-button__label,
    & > .lsn-loader-dropdown-button__chevron
    {
        @apply cursor-not-allowed;
    }

    background-image: url("data:image/gif;base64,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");
    background-repeat: no-repeat;
    background-position: center center;
}

.lsn-loader-dropdown-button--loading > .lsn-loader-dropdown-button__label
{
    @apply invisible;
}

.lsn-loader-dropdown-button__label
{
    @apply flex items-center self-stretch cursor-pointer pl-6 pr-6
        outline-none transition-colors;

    &:hover,
    &:focus
    {
        @apply bg-white/20;
    }
}

.lsn-loader-dropdown-button__separator
{
    @apply self-stretch w-px bg-current opacity-30;
}

.lsn-loader-dropdown-button__chevron
{
    @apply flex items-center self-stretch cursor-pointer pl-2 pr-2
        outline-none transition-colors;

    &:hover,
    &:focus
    {
        @apply bg-white/20;
    }
}

.lsn-btn--disabled .lsn-loader-dropdown-button__separator,
.lsn-btn--disabled .lsn-loader-dropdown-button__chevron
{
    @apply text-gray-400;
}

.lsn-loader-dropdown-button__dropdown
{
    @apply absolute top-full left-0 z-10 mt-1 -ml-2 rounded-lg shadow-lg
        min-w-[200px] max-w-[500px] overflow-hidden
        bg-white text-sm;
}

.lsn-loader-dropdown-button__item
{
    @apply px-4 py-2 cursor-pointer text-gray-600 truncate outline-none;

    &:hover,
    &:focus
    {
        @apply bg-gray-100 text-gray-700;
    }
}
</style>
